<script setup lang="ts">
import { provide, ref, watch, computed, onMounted, defineAsyncComponent } from 'vue'
import type { ISeoData } from '~/types'

import WidgetLayoutHeader from '~/components/widget/layout-header'
import WidgetLayoutFooter from '~/components/widget/layout-footer'
import Toaster from '~/components/ui/toast/Toaster.vue'
import AppProviderReviveAd from '~/components/app/provider/revive-ad'
import WidgetReviveAd from '~/components/widget/revive-ad'
import { AuthModalsProvider } from '~/components/feature/auth'
import { FeatureTypoReport } from '~/components/feature/typo-report'
import { PreviewPlaceholder } from '~/components/ui/preview-placeholder'
import { getSiteNames } from '~/lib/constants'

const LazyWidgetAiChat = defineAsyncComponent(() => import('~/components/widget/ai-chat/WidgetAiChat.vue'))

const route = useRoute()
const isEmbeddedPreview = useEmbeddedPreview()
const config = useRuntimeConfig()
const { t, locale } = useI18n()

const stickyRatesTriggerEl = ref<HTMLElement | null>(null)
provide('stickyRatesTriggerEl', stickyRatesTriggerEl)

const currencyStore = useCurrencyStore()
const currencyPromise = currencyStore.fetchCurrencies()

watch(locale, async () => {
  await clearNuxtData('/currencies')
  currencyStore.markAsStale()
  await currencyStore.fetchCurrencies()
})

const fingerprintStore = useFingerprintStore()
const siteNames = getSiteNames()

const { user, isProfileIncomplete } = useUser()
const { logout, redirectAfterAuth } = useAuth()
const { checkAndPromptPolicy } = usePrivacyPolicy()

const { isExcludedPage, getExcludedPageSeo } = useSeoExcludedPages()
const seo = ref<ISeoData | null>(null)

/**
 * Initial SSR fetch
 */
if (!isExcludedPage(route.path)) {
  const { data } = await useFetch<ISeoData>('/api/seo', {
    params: { path: route.path },
    key: `seo-${route.path}`
  })

  seo.value = data.value ?? null
}

await currencyPromise

/**
 * Cache SEO globally (prevent duplicate fetches)
 */
const nuxtApp = useNuxtApp()
nuxtApp._seoData = seo

/* -------------------- CLIENT SEO UPDATE -------------------- */

const fetchSeoDataOnClient = async (path: string) => {
  if (isExcludedPage(path)) {
    seo.value = null
    return
  }

  try {
    seo.value = await $fetch<ISeoData>('/api/seo', {
      params: { path }
    })
  } catch (e) {
    console.error('SEO fetch error:', e)
    seo.value = null
  }
}

watch(
  () => route.path,
  async (newPath, oldPath) => {
    if (newPath !== oldPath) {
      await fetchSeoDataOnClient(newPath)
    }
  }
)

const baseUrl = config.public.siteUrl
const { getAlternateLinks, getPageQuery } = useAlternateLinks()

const canonicalUrl = computed(() => {
  // Fully decode the path to handle double-encoded URLs (e.g. %253F → %3F → ?)
  let decoded = route.path
  try {
    let prev = ''
    while (prev !== decoded) {
      prev = decoded
      decoded = decodeURIComponent(decoded)
    }
  } catch {
    // If decoding fails, use whatever we have
  }
  const clean = decoded.includes('?') ? decoded.split('?')[0] : decoded
  const path = clean === '/' ? '/' : clean.replace(/\/$/, '')
  return baseUrl + encodeURI(path) + getPageQuery()
})

// Every page states its canonical URL and its language versions, also when it has no SEO
// settings of its own (detail pages): the backend sitemap lists them with the same URLs
const pageLinks = computed(() => [
  { rel: 'canonical', href: canonicalUrl.value },
  ...getAlternateLinks().map((l) => ({
    rel: l.rel,
    hreflang: l.hreflang,
    href: l.href
  }))
])

const seoHead = computed(() => {
  const excludedSeo = getExcludedPageSeo(route.path)

  /**
   * EXCLUDED PAGES
   */
  if (excludedSeo) {
    const title = `${excludedSeo.title} ${t('seo.suffix.banks_kyrgyzstan')}`
    const description = excludedSeo.description
    const ogImage = baseUrl + config.public.defaultOgImage

    return {
      htmlAttrs: { lang: locale.value },
      title,
      meta: [
        // Login, registration and the personal account are of no use in search results
        { name: 'robots', content: 'noindex, follow' },
        { name: 'description', content: description },

        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:image', content: ogImage },
        { property: 'og:image:width', content: '1080' },
        { property: 'og:image:height', content: '1080' },
        { property: 'og:site_name', content: siteNames[locale.value] },

        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImage },
        { name: 'twitter:site', content: '@banks_kg' }
      ],
      link: pageLinks.value
    }
  }

  /**
   * NORMAL PAGES
   */
  if (!seo.value) {
    return {
      htmlAttrs: { lang: locale.value },
      link: pageLinks.value
    }
  }

  const title = `${seo.value.meta_title || seo.value.title || ''} ${t(
    'seo.suffix.banks_kyrgyzstan'
  )}`

  return {
    htmlAttrs: { lang: locale.value },
    title,
    meta: [
      seo.value.robots && { name: 'robots', content: seo.value.robots },
      seo.value.meta_description && {
        name: 'description',
        content: seo.value.meta_description
      },
      seo.value.meta_keywords && { name: 'keywords', content: seo.value.meta_keywords },

      seo.value.og_title && { property: 'og:title', content: seo.value.og_title },
      seo.value.og_description && {
        property: 'og:description',
        content: seo.value.og_description
      },

      seo.value.og_image_rectangle?.url && {
        property: 'og:image',
        content: seo.value.og_image_rectangle.url
      },
      seo.value.og_image_rectangle?.width && {
        property: 'og:image:width',
        content: seo.value.og_image_rectangle.width
      },
      seo.value.og_image_rectangle?.height && {
        property: 'og:image:height',
        content: seo.value.og_image_rectangle.height
      },

      { property: 'og:site_name', content: siteNames[locale.value] },

      { name: 'twitter:card', content: 'summary_large_image' },
      seo.value.og_title && { name: 'twitter:title', content: seo.value.og_title },
      seo.value.og_description && {
        name: 'twitter:description',
        content: seo.value.og_description
      },
      seo.value.og_image_rectangle?.url && {
        name: 'twitter:image',
        content: seo.value.og_image_rectangle.url
      },
      { name: 'twitter:site', content: '@banks_kg' }
    ].filter(Boolean),
    link: pageLinks.value
  }
})

useHead(seoHead)

// Site identity; without it the first organization on a page (a bank, an MFC) becomes the site publisher
const siteIdentityId = `${baseUrl}/#identity`

useSchemaOrg([
  {
    '@type': 'Organization',
    '@id': siteIdentityId,
    name: siteNames[locale.value],
    url: baseUrl,
    logo: `${baseUrl}/icon-512.png`,
    sameAs: [
      'https://www.facebook.com/portal.banks.kg',
      'https://www.instagram.com/banks.kg',
      'https://t.me/Banks_KG',
      'https://x.com/banks_kg'
    ]
  },
  {
    '@type': 'WebSite',
    '@id': `${baseUrl}${locale.value === 'ru' ? '' : `/${locale.value}`}#website`,
    name: siteNames[locale.value],
    url: `${baseUrl}${locale.value === 'ru' ? '' : `/${locale.value}`}`,
    publisher: { '@id': siteIdentityId },
    inLanguage: locale.value,
    workTranslation: [
      {
        '@type': 'WebSite',
        '@id': `${baseUrl}#website`,
        inLanguage: 'ru',
        name: siteNames.ru
      },
      {
        '@type': 'WebSite',
        '@id': `${baseUrl}/ky#website`,
        inLanguage: 'ky',
        name: siteNames.ky
      }
    ]
  }
])

/* -------------------- INIT -------------------- */

const { initializeReverbOnInteraction } = useReverb()

onNuxtReady(() => {
  try {
    initializeReverbOnInteraction()
  } catch (e) {
    console.error('WebSocket init error:', e)
  }
})

onMounted(async () => {
  await fingerprintStore.initializeFingerprint()
  if (user.value) {
    await checkAndPromptPolicy(async () => {
      if (isProfileIncomplete.value) await redirectAfterAuth()
    }, logout)
  }
})
</script>

<template>
  <NuxtLoadingIndicator color="#3b82f6" />
  <AppProviderReviveAd>
    <PreviewPlaceholder
      v-if="isEmbeddedPreview"
      class="h-20 border-b-2"
      title="Шапка сайта"
      description="меню, поиск и курсы валют"
    />
    <WidgetLayoutHeader v-else />
    <NuxtPage class="z-0" :trigger-el="stickyRatesTriggerEl" />
    <PreviewPlaceholder
      v-if="isEmbeddedPreview"
      class="mt-10 h-32 border-t-2"
      title="Подвал сайта"
      description="ссылки, контакты и подписка"
    />
    <WidgetLayoutFooter v-else />
    <Toaster />
    <ClientOnly v-if="!isEmbeddedPreview">
      <WidgetReviveAd zoneid="37" />
      <LazyWidgetAiChat />
    </ClientOnly>
    <AuthModalsProvider />
    <FeatureTypoReport />
  </AppProviderReviveAd>
</template>
